import { useEffect, useState } from "react";
import { studioAPI, tryParseJSON } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { TrendingUp, Flame, ChevronRight, RefreshCw, Copy } from "lucide-react";

export default function Trends() {
    const { lang } = useApp();
    const [busy, setBusy] = useState(false);
    const [raw, setRaw] = useState("");
    const parsed = tryParseJSON(raw);

    const load = async (force = false) => {
        setBusy(true);
        try {
            const { result } = await studioAPI.trends(lang);
            setRaw(result);
        } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
        finally { setBusy(false); }
    };

    useEffect(() => { load(); }, [lang]);

    return (
        <div data-testid="trends-container" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
                <div>
                    <div className="overline">09 · Trend Intel</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Boost tendances hebdo" : "Weekly Trending Boost"}</h2>
                    <p className="text-sm text-slate-400 mt-2 max-w-xl">{lang === "fr" ? "Les 8 tendances Shorts qui cartonnent cette semaine + 3 formats émergents. Rafraîchi toutes les 12h." : "Top 8 Shorts trends of the week + 3 emerging formats. Refreshed every 12h."}</p>
                </div>
                <button data-testid="trends-refresh-btn" onClick={() => load(true)} disabled={busy}
                    className="self-start sm:self-end px-4 py-2.5 rounded border border-[#253046] hover:border-[#FF4D00] text-sm text-slate-200 hover:text-white kinetic flex items-center gap-2">
                    <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {lang === "fr" ? "Actualiser" : "Refresh"}
                </button>
            </div>

            {busy && !parsed && (
                <div className="rounded border border-[#253046] bg-[#0B111B] p-16 text-center text-slate-400 font-mono-sf text-xs">SCANNING TIKTOK · YOUTUBE · REELS ···</div>
            )}

            {parsed && (
                <>
                    <div className="flex items-center gap-4 flex-wrap">
                        {parsed.week && (
                            <div className="px-3 py-1.5 rounded-full border border-[#FF4D00]/40 bg-[#FF4D00]/10">
                                <span className="font-mono-sf text-[10px] text-[#FF4D00]">{parsed.week}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-2 text-[10px] font-mono-sf text-[#10B981]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] rec-dot" /> LIVE INTEL
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-3">
                        {(parsed.trends || []).map((tr) => (
                            <div key={tr.rank} data-testid={`trend-card-${tr.rank}`} className="bg-[#161D2A] border border-[#253046] hover:border-[#FF4D00]/50 kinetic rounded-lg p-5">
                                <div className="flex items-start gap-4">
                                    <div className="font-display text-4xl font-black text-[#FF4D00] leading-none">{String(tr.rank).padStart(2, "0")}</div>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <h3 className="font-display text-lg font-bold uppercase leading-tight">{tr.title}</h3>
                                            {tr.difficulty_1_5 && (
                                                <div className="flex gap-0.5 shrink-0 mt-1">
                                                    {[1, 2, 3, 4, 5].map((n) => (
                                                        <Flame key={n} className={`w-3 h-3 ${n <= tr.difficulty_1_5 ? "text-[#FF4D00] fill-[#FF4D00]" : "text-slate-700"}`} />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{tr.why_it_works}</p>
                                        <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] font-mono-sf">
                                            {tr.format && <div className="px-2 py-1 rounded bg-[#0099FF]/10 text-[#0099FF] uppercase truncate">{tr.format}</div>}
                                            {tr.example_niche && <div className="px-2 py-1 rounded bg-[#FFB800]/10 text-[#FFB800] uppercase truncate">{tr.example_niche}</div>}
                                        </div>
                                        {tr.hook_template && (
                                            <div className="mt-3 bg-[#0B111B] border-l-2 border-[#FFB800] rounded-r p-2.5">
                                                <div className="flex items-center justify-between">
                                                    <div className="overline">Hook template</div>
                                                    <button onClick={() => { navigator.clipboard.writeText(tr.hook_template); toast.success("Copié"); }} className="text-slate-500 hover:text-white"><Copy className="w-3 h-3" /></button>
                                                </div>
                                                <div className="text-xs text-white mt-1 italic">« {tr.hook_template} »</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {parsed.emerging_formats?.length > 0 && (
                        <div className="bg-gradient-to-br from-[#1E2738] to-[#0B111B] border border-[#FF4D00]/30 rounded-lg p-5">
                            <div className="overline">{lang === "fr" ? "Formats émergents à surveiller" : "Emerging formats to watch"}</div>
                            <div className="mt-3 grid sm:grid-cols-3 gap-3">
                                {parsed.emerging_formats.map((f, i) => (
                                    <div key={i} className="bg-[#080B10] border border-[#253046] rounded p-3 flex gap-2">
                                        <ChevronRight className="w-4 h-4 text-[#FF4D00] shrink-0 mt-0.5" />
                                        <div className="text-sm text-slate-200">{f}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
